import { useAppDispatch, useAppSelector } from '@app';
//~ Import the selectors from your slice file
import { SkipOrArgs } from '@common/types/queryState';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { useSelectCollection } from '@features/Collection';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
    selectIsClipSelected,
    selectSelectedClipId,
    setSelectedClipId,
} from '../slices/clipEntitySlice';
import { Clip, DEFAULT_CLIP } from '../types';
import { useClipById, UseClipByIdArgs } from './useClipById';
import { useClipCollectionResolver } from './useClipCollectionResolver';

//~ You can also define individual hooks for each selector here

export const useSelectedClipId = () => {
    const storeId = useAppSelector(selectSelectedClipId);
    const { assetId } = useParams<{ assetId: string }>();
    return assetId || storeId || null;
};

export const useIsClipSelected = () => {
    return useAppSelector(selectIsClipSelected);
};

/**
 * Custom hook to access selectors for the collectionEntity slice.
 *
 * @returns An object containing selector functions bound to useSelector.
 */
export const useSelectedClip = () => {
    const { selectCollection } = useSelectCollection();
    //~ Define selector functions for relevant properties here

    const isClipSelected = useIsClipSelected();
    const selectedClipId = useSelectedClipId();

    const queryArgs: SkipOrArgs<UseClipByIdArgs> = useMemo(() => {
        if (!selectedClipId) return skipToken;
        return { assetId: selectedClipId };
    }, [selectedClipId]);

    const { data, ...queryResult } = useClipById(undefined, queryArgs);
    const { collectionId } = useClipCollectionResolver(undefined, queryArgs);

    useEffect(() => {
        if (collectionId) {
            selectCollection(collectionId);
        }
    }, [collectionId, selectCollection]);

    const selectedClip = useMemo(() => {
        return mergeWithDefault(data, DEFAULT_CLIP);
    }, [data]);

    return {
        //~ Return all the defined selector values here
        isClipSelected,
        selectedClipId,
        selectedClip,
        ...queryResult,
    };
};

export const useSelectClip = () => {
    const dispatch = useAppDispatch();
    const selectClip = useCallback(
        (clipId: Clip['id']) => dispatch(setSelectedClipId(clipId)),
        [dispatch],
    );

    return { selectClip };
};
